import DevicePairing, { type DeployDockPayload } from '@/composables/Device/DevicePairing'
import { useDeployProgressSocket } from '@/composables/Device/useDeployProgressSocket'
import { useDeployProgressStore } from '@/store/deployProgress'
import { rcTypes } from '@/constants/deviceManager'
import type { deployData } from '@/types/devices.types'

/**
 * Minimal device shape every deploy caller can supply. `Device`
 * (device list) and `OnboardingDevice` (admin org list) both satisfy it.
 */
export type DeployTargetDevice = {
  deviceID: string
  deviceType: string
  deviceGroupID?: string
}

/**
 * Shared dock-deploy orchestration (FLY3D-1064).
 *
 * The same three steps were duplicated in `deployDockModal.vue` and
 * `deviceManagement.vue`: build the GHA payload from a decrypted deploy
 * code, subscribe to the progress socket, dispatch. This composable owns
 * that sequence so the admin "Deploy Dock" tab is not a third copy.
 * Migrating the two existing callers onto it is a follow-up.
 */
export default () => {
  const { deployDock } = DevicePairing()
  const deploySocket = useDeployProgressSocket()
  const deployProgressStore = useDeployProgressStore()

  /**
   * Same RC resolution the deploy modal uses: prefer the RC device in
   * the dock's group, fall back to the DJI_DOCK_3 `${deviceID}_rc`
   * naming convention, otherwise empty.
   */
  const resolveRcId = (
    device: DeployTargetDevice,
    siblingDevices: DeployTargetDevice[] = [],
  ): string => {
    if (device.deviceGroupID) {
      const rcDevice = siblingDevices.find(
        (d) => d.deviceGroupID === device.deviceGroupID && rcTypes.includes(d.deviceType),
      )
      if (rcDevice) return rcDevice.deviceID
    }
    if (device.deviceType === 'DJI_DOCK_3') return `${device.deviceID}_rc`
    return ''
  }

  /** Field mapping from a decrypted deploy code to the GHA workflow inputs. */
  const buildDeployPayload = (
    device: DeployTargetDevice,
    data: deployData,
    opts: { organisationId: string; rcId: string },
  ): DeployDockPayload => ({
    machine: data.machine,
    mqtt_host: data.mqttHost,
    run_docker_compose: data.composeup,
    dock_id: device.deviceID,
    rc_id: opts.rcId,
    organisation_id: opts.organisationId,
    dock_type: device.deviceType,
    dji_gateway_sn: data.gatewaySN,
  })

  /**
   * Resets the progress entry, subscribes to the deploy feed and then
   * dispatches the GitHub Actions workflow.
   *
   * FLY3D-612: subscribe BEFORE dispatching. IFB-65 only delivers
   * broadcasts to subscribers whose correlation_id matches the deploy
   * run; if we dispatch first, early events fan out before we're
   * registered and are dropped. `start()` resolves on the subscribe ack
   * so it is safe to dispatch the moment it returns. On a failed
   * dispatch the subscription is torn down so no socket is left waiting
   * for events that will never arrive.
   */
  const dispatchDeploy = async (
    deviceId: string,
    payload: DeployDockPayload,
  ): Promise<{ success: boolean; correlationId: string; error?: string }> => {
    deployProgressStore.reset(deviceId)
    // Seed startedAt so the elapsed timer ticks from dispatch time, not
    // from the first WS payload, and survives a panel close/reopen.
    deployProgressStore.ensureEntry(deviceId).startedAt = Date.now()
    const correlationId = crypto.randomUUID()
    await deploySocket.start(deviceId, correlationId)
    // FLY3D-1085 — deployDock validates against the payload schema and
    // the broker-host guard before dispatching, and returns the reason.
    const { success, error } = await deployDock(payload, correlationId)
    if (!success) {
      deploySocket.stop(deviceId, 'dispatch-failed')
    }
    return { success, correlationId, error }
  }

  return {
    resolveRcId,
    buildDeployPayload,
    dispatchDeploy,
    entryFor: deploySocket.entryFor,
  }
}
